/**
 * System event envelope — wire contract between internal producers
 * (cirium-integration and other future producers) and the
 * `system_event_handler` in `apps/services/conversation`.
 *
 * Producers publish to the dedicated SQS queue
 * (`intelligence-conversation-system-events`); the consumer parses
 * with `.strict()` so any unrecognised field routes to the DLQ rather
 * than silently succeeding.
 *
 * This type is deliberately distinct from `MessageEnvelope` (D-02):
 * system events originate from internal services, not from end-users
 * via a chat channel, so they carry different identity/routing fields.
 *
 * `eventKey` (optional) is a content-derived dedup identity — a non-PII hash a
 * producer stamps so an exact re-import of the same source event is recognised
 * downstream and not re-notified. Optional + `.strict()` keeps every existing
 * producer (which omits it) backward-compatible; the producer owns the format.
 */
import { z } from 'zod';

import { EnvelopeOrgIdSchema } from './org_id';

export const SystemEventEnvelopeSchema = z
  .object({
    envelope: z.object({ version: z.literal(1) }).strict(),
    messageType: z.string().min(1).max(100),
    userId: z.string().min(1).max(200),
    orgId: EnvelopeOrgIdSchema,
    timestamp: z.coerce.date(),
    metadata: z.record(z.string(), z.unknown()),
    eventKey: z.string().optional(),
  })
  .strict();

export type SystemEventEnvelope = z.infer<typeof SystemEventEnvelopeSchema>;
